import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Download, FileText, CheckCircle, AlertCircle, Loader2, Layers, Route, Link2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { label: 'Nouveau', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  error: { label: 'Erreur', className: 'bg-red-50 text-red-700 border-red-200' },
};

export default function TransferCSVImportModal({ 
  open, 
  onOpenChange, 
  contractId, 
  contractNumber,
  clientName: clientNameProp = '',
  companyName: companyNameProp = '',
  accountType: accountTypeProp = 'entreprise',
  operations = [], // Opérations existantes du contrat
  onSuccess 
}) {
  const [step, setStep] = useState('upload'); // upload, config, preview, result
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [contractInfo, setContractInfo] = useState(null); // données enrichies depuis le preview API
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Configuration du groupement
  const [groupingMode, setGroupingMode] = useState('by_route');
  const [selectedOperationId, setSelectedOperationId] = useState('');
  
  const fileRef = useRef(null);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setStats(null);
    setResult(null);
    setGroupingMode('by_route');
    setSelectedOperationId('');
    setContractInfo(null);
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.endsWith('.csv')) {
      toast({ title: 'Format invalide', description: 'Veuillez sélectionner un fichier .csv', variant: 'destructive' });
      return;
    }
    setFile(selected);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selected);
      const res = await axios.post(`${API}/import/transfers/${contractId}/preview`, formData);
      setPreview(res.data.preview);
      setStats(res.data.stats);
      if (res.data.contract) setContractInfo(res.data.contract);
      setStep('config');
    } catch (err) {
      toast({ title: 'Erreur de lecture', description: err.response?.data?.detail || 'Impossible de lire le fichier', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToPreview = () => {
    if (groupingMode === 'existing' && !selectedOperationId) {
      toast({ title: 'Sélection requise', description: 'Veuillez sélectionner une opération existante', variant: 'destructive' });
      return;
    }
    setStep('preview');
  };

  const handleConfirm = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams();
      params.append('grouping_mode', groupingMode);
      if (groupingMode === 'existing' && selectedOperationId) {
        params.append('parent_operation_id', selectedOperationId);
      }
      
      const res = await axios.post(
        `${API}/import/transfers/${contractId}/confirm?${params.toString()}`, 
        formData
      );
      setResult(res.data);
      setStep('result');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || "Erreur lors de l'import", variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/import/transfers/template`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-[800px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-red-600" />
            Importer des Transferts (CSV)
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Sélectionnez un fichier CSV à importer'}
            {step === 'config' && 'Configurez le mode de regroupement des transferts'}
            {step === 'preview' && 'Vérifiez les données avant de confirmer'}
            {step === 'result' && "Résultat de l'import"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-700">Contrat: {contractNumber}</p>
              </div>
              
              <div
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-red-300 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
                data-testid="csv-dropzone"
              >
                {loading ? (
                  <Loader2 className="w-10 h-10 mx-auto text-red-500 animate-spin" />
                ) : (
                  <>
                    <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-700">Cliquez pour sélectionner un fichier CSV</p>
                    <p className="text-xs text-gray-400 mt-1">Séparateur : point-virgule (;) ou virgule (,)</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="csv-file-input"
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="w-full" data-testid="csv-download-template">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le modèle CSV
              </Button>
            </div>
          )}

          {/* Step 2: Configuration */}
          {step === 'config' && stats && (
            <div className="space-y-6">
              {/* Client / Entreprise info */}
              {(() => {
                const displayCompany = contractInfo?.companyName || companyNameProp;
                const displayClient = contractInfo?.clientName || clientNameProp;
                const isEntreprise = ['entreprise', 'company'].includes(contractInfo?.accountType || accountTypeProp);
                return (
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                        {isEntreprise ? 'Entreprise' : 'Client'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {isEntreprise ? (displayCompany || displayClient || '—') : (displayClient || '—')}
                      </p>
                      {isEntreprise && displayClient && displayClient !== displayCompany && (
                        <p className="text-xs text-gray-400 truncate">{displayClient}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">Contrat</p>
                      <p className="text-sm font-semibold text-gray-700">{contractNumber}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-xs text-gray-500">Lignes totales</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{stats.valid}</p>
                  <p className="text-xs text-emerald-600">Valides</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{stats.unique_routes}</p>
                  <p className="text-xs text-blue-600">Trajets uniques</p>
                </div>
              </div>

              {/* Grouping Mode */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Mode de regroupement des opérations</h3>
                
                <RadioGroup value={groupingMode} onValueChange={setGroupingMode} className="space-y-3">
                  <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${groupingMode === 'single' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <RadioGroupItem value="single" id="single" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="single" className="flex items-center gap-2 cursor-pointer">
                        <Layers className="w-4 h-4 text-red-600" />
                        <span className="font-medium">Une seule opération pour tous</span>
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Créer 1 opération mère commune pour les {stats.valid} transferts
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${groupingMode === 'by_route' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <RadioGroupItem value="by_route" id="by_route" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="by_route" className="flex items-center gap-2 cursor-pointer">
                        <Route className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Grouper par trajet</span>
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Créer {stats.unique_routes} opération(s) - une par combinaison départ/arrivée unique
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${groupingMode === 'existing' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <RadioGroupItem value="existing" id="existing" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="existing" className="flex items-center gap-2 cursor-pointer">
                        <Link2 className="w-4 h-4 text-purple-600" />
                        <span className="font-medium">Associer à une opération existante</span>
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Ajouter tous les transferts à une opération déjà créée
                      </p>
                      
                      {groupingMode === 'existing' && (
                        <div className="mt-3">
                          <Select value={selectedOperationId} onValueChange={setSelectedOperationId}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Sélectionner une opération..." />
                            </SelectTrigger>
                            <SelectContent>
                              {operations.length === 0 ? (
                                <SelectItem value="_none" disabled>Aucune opération disponible</SelectItem>
                              ) : (
                                operations.map(op => (
                                  <SelectItem key={op.id} value={op.id}>
                                    {op.operationNumber} - {op.departure} → {op.arrival}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-3">
              {/* Contract info recap */}
              {(() => {
                const displayCompany = contractInfo?.companyName || companyNameProp;
                const displayClient = contractInfo?.clientName || clientNameProp;
                const isEntreprise = ['entreprise', 'company'].includes(contractInfo?.accountType || accountTypeProp);
                return (
                  <div className="flex items-center gap-2 bg-gray-50 rounded px-3 py-2 text-xs text-gray-600 border border-gray-200">
                    <span className="font-semibold text-gray-800">
                      {isEntreprise ? (displayCompany || displayClient) : displayClient}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span>{contractNumber}</span>
                    <span className="text-gray-300">|</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isEntreprise ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {isEntreprise ? 'Entreprise' : 'Particulier'}
                    </span>
                  </div>
                );
              })()}

              {/* Summary */}
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800">
                  {groupingMode === 'single' && `Création d'une opération mère commune pour ${stats.valid} transferts`}
                  {groupingMode === 'by_route' && `Création de ${stats.unique_routes} opération(s) mère(s) groupées par trajet`}
                  {groupingMode === 'existing' && `Association de ${stats.valid} transferts à l'opération sélectionnée`}
                </p>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-10 text-xs">#</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Départ</TableHead>
                      <TableHead className="text-xs">Arrivée</TableHead>
                      <TableHead className="text-xs">Poids</TableHead>
                      <TableHead className="text-xs">Chauffeur</TableHead>
                      <TableHead className="text-xs">Plaque</TableHead>
                      <TableHead className="text-xs">Opérateur</TableHead>
                      <TableHead className="text-xs">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, idx) => {
                      const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.new;
                      return (
                        <TableRow key={idx} className={row.status === 'error' ? 'bg-red-50/50' : ''}>
                          <TableCell className="text-xs text-gray-400">{row.line}</TableCell>
                          <TableCell className="text-xs">{row.date}</TableCell>
                          <TableCell className="text-xs max-w-[100px] truncate" title={row.departure}>{row.departure}</TableCell>
                          <TableCell className="text-xs max-w-[100px] truncate" title={row.arrival}>{row.arrival}</TableCell>
                          <TableCell className="text-xs">{row.weight ? `${row.weight}T` : '-'}</TableCell>
                          <TableCell className="text-xs">{row.driverName || '-'}</TableCell>
                          <TableCell className="text-xs font-mono text-gray-600">{row.vehiclePlate || '-'}</TableCell>
                          <TableCell className="text-xs">{row.operatorName || '-'}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${cfg.className}`}>{row.action}</Badge>
                              {row.errors?.length > 0 && (
                                <span className="text-[10px] text-red-500">{row.errors.join(', ')}</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {preview.length < stats.total && (
                <p className="text-xs text-gray-500 text-center">
                  Aperçu limité à 50 lignes sur {stats.total}
                </p>
              )}
            </div>
          )}

          {/* Step 4: Result */}
          {step === 'result' && result && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                <p className="text-lg font-semibold text-gray-900">Import terminé</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{result.created}</p>
                  <p className="text-xs text-emerald-600">Transferts créés</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{result.operations_created}</p>
                  <p className="text-xs text-blue-600">Opérations créées</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                  <p className="text-xs text-red-600">Erreurs</p>
                </div>
              </div>
              
              {result.errors?.length > 0 && (
                <div className="border border-red-100 rounded-lg p-3 space-y-1 max-h-[150px] overflow-y-auto">
                  <p className="text-xs font-medium text-red-700">Détail des erreurs :</p>
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">Ligne {err.line}: {err.errors.join(', ')}</p>
                  ))}
                </div>
              )}

              {result.created_runs?.length > 0 && (
                <div className="border border-emerald-100 rounded-lg p-3 space-y-1 max-h-[150px] overflow-y-auto">
                  <p className="text-xs font-medium text-emerald-700">Transferts créés :</p>
                  {result.created_runs.map((run, i) => (
                    <p key={i} className="text-xs text-emerald-600">
                      {run.shortReference || run.runNumber}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {step === 'upload' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          )}
          {step === 'config' && (
            <>
              <Button variant="outline" onClick={reset}>Recommencer</Button>
              <Button
                onClick={handleContinueToPreview}
                disabled={groupingMode === 'existing' && !selectedOperationId}
                className="bg-red-600 hover:bg-red-700"
              >
                Continuer
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('config')}>Retour</Button>
              <Button
                onClick={handleConfirm}
                disabled={loading || stats?.valid === 0}
                className="bg-red-600 hover:bg-red-700"
                data-testid="csv-confirm-import"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Importer {stats?.valid} transfert(s)
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={() => { onOpenChange(false); reset(); }} className="bg-emerald-600 hover:bg-emerald-700">
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
